/**
 * The token from an emailed sign-in or password reset link
 * (`…/auth/magic-link#token=<token>`). The API puts it in the fragment so it
 * never reaches a server access log or a Referer header.
 *
 * The token is taken out of the address bar by `captureLinkToken`, which
 * `instrument.ts` calls before Sentry starts. Sentry records the URL a
 * `history.replaceState` moves away from, fragment included, so a token removed
 * any later would ride along on the next error report. A reset token is good
 * for 60 minutes, so that is an account takeover for anyone who can read
 * Sentry. Its only import, `redirectPath.ts`, has no dependencies, so importing
 * it cannot run anything first.
 *
 * A sign-in link can also carry `&to=<path>`, where to go once signed in. It is
 * captured with the token, because removing the token removes the whole
 * fragment. It is not a secret, but nothing here logs it either.
 */

import { safeRedirectPath } from '~/auth/redirectPath';

let capturedToken: string | null = null;
let capturedDestination: string | null = null;

const fragmentParams = () => new URLSearchParams(window.location.hash.slice(1));

const tokenFromUrl = (): string | null => fragmentParams().get('token') || null;

/**
 * Drop the link token from the address bar, and so from browser history. Keeps
 * the router's history state.
 */
export const removeLinkTokenFromUrl = () => {
  if (window.location.hash) {
    const { pathname, search } = window.location;
    window.history.replaceState(window.history.state, '', `${pathname}${search}`);
  }
};

/**
 * Keep this page's link token in memory and remove it from the URL. Call once,
 * when the document loads and before Sentry is initialised.
 */
export const captureLinkToken = () => {
  const params = fragmentParams();
  capturedToken = params.get('token') || null;
  capturedDestination = capturedToken ? params.get('to') : null;
  if (capturedToken) {
    removeLinkTokenFromUrl();
  }
};

/**
 * The link token this page was opened with. Pure, so it is safe as a
 * `useState` initializer. A token still in the URL wins; that only happens
 * where `captureLinkToken` did not run, such as a test.
 */
export const readLinkToken = (): string | null => tokenFromUrl() ?? capturedToken;

/**
 * The `to` path from the link this page was opened with, if it is a path on
 * this site; see `safeRedirectPath`. Read from wherever `readLinkToken` reads
 * the token, so the two always come from the same link.
 */
export const readLinkDestination = (): string | null => {
  const params = fragmentParams();
  return safeRedirectPath(params.get('token') ? params.get('to') : capturedDestination);
};
